<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-kaia-logo="false"
    :show-lang-picker="false"
  >
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__self_test_pre_subscription_complete__title') }}
        </p>
        <p class="kds-typography-paragraph-large kds-color-text-secondary">
          {{ $t('eu__self_test_pre_subscription_complete__subtitle') }}
        </p>
      </div>

      <div class="program-summary">
        <div class="program-summary__tile">
          <div
            class="program-summary__icon program-summary__icon--pain-location"
          >
            <img
              :src="painLocationIcon"
              alt=""
              class="program-summary__icon-img"
            />
          </div>
          <div>
            <p class="kds-typography-paragraph-large kds-color-text-tertiary">
              {{
                $t(
                  'eu__self_test_pre_subscription_complete__pain_location__label',
                )
              }}
            </p>
            <p class="kds-typography-title-large">
              {{ $t(painLocationKey) }}
            </p>
          </div>
        </div>

        <div class="program-summary__tile">
          <div class="program-summary__icon program-summary__icon--focus">
            <img
              :src="focusIcon"
              alt=""
              class="program-summary__icon-img"
            />
          </div>
          <div>
            <p class="kds-typography-paragraph-large kds-color-text-tertiary">
              {{
                $t(
                  'eu__self_test_pre_subscription_complete__training_focus__label',
                )
              }}
            </p>
            <p class="kds-typography-title-large">
              {{ $t(trainingFocusKey) }}
            </p>
          </div>
        </div>

        <div
          v-if="goalKey"
          class="program-summary__tile"
        >
          <div class="program-summary__icon program-summary__icon--goal">
            <img
              :src="goalIcon"
              alt=""
              class="program-summary__icon-img"
            />
          </div>
          <div>
            <p class="kds-typography-paragraph-large kds-color-text-tertiary">
              {{ $t('eu__self_test_pre_subscription_complete__goal__label') }}
            </p>
            <p class="kds-typography-title-large">
              {{ $t(goalKey) }}
            </p>
          </div>
        </div>

        <div class="program-summary__tile">
          <div class="program-summary__icon program-summary__icon--frequency">
            <img
              :src="frequencyIcon"
              alt=""
              class="program-summary__icon-img"
            />
          </div>
          <div>
            <p class="kds-typography-paragraph-large kds-color-text-tertiary">
              {{
                $t(
                  'eu__self_test_pre_subscription_complete__recommended_frequency__label',
                )
              }}
            </p>
            <p class="kds-typography-title-large">
              {{
                $t(
                  'eu__self_test_pre_subscription_complete__recommended_frequency__value',
                )
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <BaseBanner
      v-if="message.show"
      :variant="message.type"
      :title="message.title"
    >
      <div v-html="message.text" />
    </BaseBanner>

    <template #actions>
      <BaseButton
        data-qa="submit"
        :loading="isSubmitting"
        @click="next"
      >
        {{ $t(ctaKey) }}
      </BaseButton>
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env.js'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import useAssessmentStore from '@shared/modules/assessment/stores/useAssessmentStore.js'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import useUserStore from '@shared/stores/useUserStore'
import {
  completeSelfTest,
  submitSelfTestAnswers,
} from '@shared/modules/assessment/submitSelfTest.js'
import painUpperBack from '@shared/assets/img/pain_upper_back_female.svg'
import painLowerBack from '@shared/assets/img/pain_lower_back_female.svg'
import frequencyIcon from '@shared/assets/img/program_overview_frequency.svg'
import goalIcon from '@shared/assets/img/program_overview_goal.svg'
import focusIcon from '@shared/assets/img/program_overview_focus.svg'

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const assessmentStore = useAssessmentStore()
const selfTestStore = useSelfTestStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const router = useRouter()
const { t } = useI18n()
const isSubmitting = ref(false)
const message = ref({
  text: '',
  title: '',
  type: 'warning',
  show: false,
})

// After activation this is the last screen, so the button starts the program
// like the congratulations screen's would; before, it continues the funnel.
const ctaKey = computed(() =>
  userStore.user?.currentSubscription != null
    ? 'b2b_self_test_commitment_intro_cta'
    : 'generic_action_continue',
)

const painLocationKey = computed(() => {
  switch (selfTestStore.worstPainLocation) {
    case 'upper-back':
      return 'eu__self_test_pre_subscription_complete__pain_location__upper_back'
    case 'lower-back':
      return 'eu__self_test_pre_subscription_complete__pain_location__lower_back'
    default:
      return 'eu__self_test_pre_subscription_complete__pain_location__upper_back'
  }
})

const painLocationIcon = computed(() => {
  switch (selfTestStore.worstPainLocation) {
    case 'upper-back':
      return painUpperBack
    case 'lower-back':
      return painLowerBack
    default:
      return painUpperBack
  }
})

const trainingFocusKey = computed(() => {
  const level = selfTestStore.painIntensity
  if (level >= 0 && level <= 3)
    return 'eu__self_test_pre_subscription_complete__training_focus__pain_low'
  if (level > 3 && level <= 6)
    return 'eu__self_test_pre_subscription_complete__training_focus__pain_moderate'
  return 'eu__self_test_pre_subscription_complete__training_focus__pain_high'
})

const goalKey = computed(() => {
  switch (selfTestStore.goal) {
    case 'relieve-pain':
      return 'eu__self_test_pre_subscription_complete__goal__relieve_pain'
    case 'fewer-pain-meds':
      return 'eu__self_test_pre_subscription_complete__goal__fewer_pain_meds'
    case 'bring-movement-back':
      return 'eu__self_test_pre_subscription_complete__goal__bring_movement_back'
    case 'build-strength':
      return 'eu__self_test_pre_subscription_complete__goal__build_strength'
    case 'sleep-better':
      return 'eu__self_test_pre_subscription_complete__goal__sleep_better'
    case 'become-more-mobile':
      return 'eu__self_test_pre_subscription_complete__goal__become_more_mobile'
    case 'prevent-pain':
      return 'eu__self_test_pre_subscription_complete__goal__prevent_pain'
    case 'avoid-surgery':
      return 'eu__self_test_pre_subscription_complete__goal__avoid_surgery'
    default:
      return null
  }
})

const next = async () => {
  if (isSubmitting.value) return

  // After activation this screen is the last step of the full self test,
  // so it completes the self test before leaving the questionnaire.
  if (userStore.user?.currentSubscription != null) {
    isSubmitting.value = true
    await completeSelfTest('full')
    isSubmitting.value = false
    props.handleNextEvent()
    return
  }

  // Before sign-up the sign-up page submits the partial with the account. An
  // in-app patient without a prescription is already signed in, so this
  // screen submits it before doctor outreach. The outreach PDF and KIM
  // message are built from these answers, so a failed submit keeps the
  // patient here to retry instead of continuing without them. Outside MSK EU
  // this screen never submits: the legacy pre-subscription set submits from
  // its own completion screen.
  if (env.isMskEu && authStore.isAuthenticated) {
    isSubmitting.value = true
    message.value.show = false
    try {
      await submitSelfTestAnswers('preSignup', { partial: true })
    } catch (error) {
      console.error(
        'Failed to submit pre-activation assessment answers:',
        error,
      )
      message.value.show = true
      message.value.type = 'warning'
      message.value.title = t('generic__error_title')
      message.value.text = t('generic__error_message', {
        email: env.VITE_SUPPORT_EMAIL,
      })
      isSubmitting.value = false
      return
    }
    isSubmitting.value = false
  }
  selfTestStore.markPreSignupComplete()
  router.push(assessmentStore.finishRoute)
}
</script>

<style lang="scss" scoped>
.program-summary {
  display: flex;
  flex-direction: column;
  gap: $kds-space-3x;
}

.program-summary__tile {
  display: flex;
  align-items: center;
  gap: $kds-space-1_5x;
}

.program-summary__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background-color: $kds-color-background-blue-strong;
}

.program-summary__icon--goal {
  background-color: $kds-color-background-purple-subtle;
}

.program-summary__icon--frequency {
  background-color: $kds-color-background-yellow-strong;
}

.program-summary__icon--pain-location .program-summary__icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
